// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import { EditBase, TopToolbar, useRecordContext, useGetOne } from 'react-admin';
import { Box, Card, CardContent, Typography, Grid, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { useNavigate, useParams } from 'react-router';
import { BillingAccountUsers } from './BillingAccountUsers/BillingAccountUsers';
import { BillingAccountProjects } from './BillingAccountProjects/BillingAccountProjects';
import { BillingAccountUsage } from './BillingAccountUsage/BillingAccountUsage';
import { BillingAccountProducts } from './BillingAccountProducts/BillingAccountProducts';
import { IProductLine, listProducts } from '../../Services/productEntitlements';
import { BillingAccountStripe } from './BillingAccountStripe/BillingAccountStripe';
import { BillingAccountBrevo } from './BillingAccountBrevo/BillingAccountBrevo';
import { BillingAccountPartner } from './BillingAccountPartner/BillingAccountPartner';
import { BillingAccountDeleteModal } from './BillingAccountDeleteModal';
import { BillingAccountFreeFlag } from './BillingAccountFreeFlag';
import { BillingAccountAgentsBetaFlag } from './BillingAccountAgentsBetaFlag';
import { BillingAccountInvoices } from './BillingAccountInvoices/BillingAccountInvoices';
import { BillingAccountContract } from './BillingAccountContract/BillingAccountContract';
import { ContractUsageCard } from './BillingAccountContract/ContractUsageCard';
import { ActivityCard } from '../Activities/ActivityCard';

const Breadcrumb = () => {
	const record = useRecordContext();
	return (
		<TopToolbar>
			<Typography variant="subtitle1">
				Home / Billing Account / {record ? record.name : 'Edit'}
			</Typography>
		</TopToolbar>
	);
};

export const BillingAccountEdit = (props: any) => {
	const { id } = useParams();
	const navigate = useNavigate();

	const { data: record, isLoading, refetch } = useGetOne('billing-accounts', { id });
	const [deleteOpen, setDeleteOpen] = useState(false);

	// Pricing v2 : les lignes produit décident du rendu. Au moins une `admin` →
	// compte sous contrat, bloc Produits éditable ; sinon Stripe pilote, et les
	// produits se lisent en lecture seule dans le bloc Stripe.
	const [productLines, setProductLines] = useState<IProductLine[]>([]);
	const loadProducts = useCallback(() => {
		if (!id) return;
		listProducts(String(id))
			.then((l) => setProductLines(l || []))
			.catch(() => setProductLines([]));
	}, [id]);
	useEffect(() => {
		loadProducts();
	}, [loadProducts]);
	// ⚠️ C'était `every(admin)`, et ça a lâché en silence le 2026-09-25. La liste
	// des produits SYNTHÉTISE une ligne `stripe` à 0 pour tout produit sans ligne
	// en base (ProductEntitlementService.List) : l'ajout de MMM au catalogue a
	// donc fait apparaître une 7ᵉ ligne « stripe » sur TOUS les comptes mis sous
	// contrat avant cette date, et le bloc Produits a disparu de leur fiche —
	// l'opérateur ne pouvait plus éditer les quantités d'un contrat.
	//
	// Le critère est celui de partout ailleurs (billingMode côté API,
	// entitlements.ContractAccountIDs dans le SDK) : AU MOINS une ligne admin.
	// Ajouter un produit au catalogue ne doit pas pouvoir masquer l'édition des
	// droits d'un compte.
	//
	// Pour recréer les lignes manquantes sur les comptes déjà sous contrat :
	// « Recalculer les droits » sur /products — le backfill traite désormais un
	// compte ayant des lignes admin comme un compte sous contrat.
	const underContract = productLines.some((l) => l.source === 'admin');

	if (isLoading) return null;
	if (!record) return null;

	// Un compte sous contrat n'a rien à faire chez Stripe : sa facture vit dans
	// Pennylane, et le bloc n'y proposerait que de lui créer un customer.
	//
	// ⚠️ On ne le masque QUE tant qu'un ABONNEMENT court encore. « Passer sous
	// contrat » ne résilie pas l'abonnement existant — c'est explicitement prévu
	// pour poser les droits pendant que la vieille sub tourne. Le cacher ferait
	// disparaître de l'écran un abonnement qui prélève le client EN PLUS de sa
	// facture Pennylane, et plus personne ne verrait qu'il faut le couper.
	//
	// Un customer Stripe résiduel, lui, ne facture rien : il ne justifie pas de
	// garder le bloc. `subscriptions` est déjà filtré aux `active`/`trialing`
	// par le back (BillingAccountRepository.FindOneById) — sa seule présence
	// suffit donc à dire « ça tourne encore ».
	//
	// ⚠️ Cette liste vient de qm_subscriptions, PAS de Stripe en direct : une
	// résiliation faite dans le dashboard n'arrive ici que par le webhook, ou
	// par « Resync from Stripe ». Et une résiliation en fin de période laisse
	// l'abonnement `active` jusqu'à son terme — le bloc reste alors, à raison.
	const hasLiveSubscription = Array.isArray(record.subscriptions) && record.subscriptions.length > 0;
	const showStripe = !underContract || hasLiveSubscription;

	return (
		<EditBase {...props}>
			<React.Fragment>
				<Breadcrumb />
				<Grid container spacing={2}>
					<Grid item xs={8}>
						{/* Tous les panels empilés sur la page d'accueil. Plus de
						    navigation par onglet — l'admin a tout sous les yeux. */}
						<BillingAccountUsage billingAccount={record} />
						{/* Sous contrat seulement : c'est la période contractuelle qui
						    découpe cette vue, et un Pay as you go n'en a pas. */}
						{underContract && (
							<Box sx={{ mt: 2 }}>
								<ContractUsageCard billingAccountId={String(record.id)} />
							</Box>
						)}
						{underContract && (
							<Box sx={{ mt: 2 }}>
								<BillingAccountProducts billingAccount={record} lines={productLines} onChanged={loadProducts} />
							</Box>
						)}
						<Box sx={{ mt: 2 }}>
							<BillingAccountProjects billingAccount={record} />
						</Box>
						<Box sx={{ mt: 2 }}>
							<BillingAccountUsers billingAccount={record} />
						</Box>
						{/* Facturé hors Stripe : ses factures se déclarent ici (Pennylane plus
						    tard). ⚠️ Marquer une facture payée émet `payment.succeeded` et crée
						    la commission du partenaire attribué — c'est le seul chemin par lequel
						    un compte sous contrat commissionne, faute d'abonnement Stripe. */}
						{underContract && (
							<Box sx={{ mt: 2 }}>
								<BillingAccountInvoices billingAccountId={String(record.id)} />
							</Box>
						)}
						{showStripe && (
							<Box sx={{ mt: 2 }}>
								<BillingAccountStripe
									billingAccount={record}
									onChanged={refetch}
									productLines={underContract ? undefined : productLines}
									onProductsChanged={loadProducts}
									underContract={underContract}
								/>
							</Box>
						)}
						<Box sx={{ mt: 2 }}>
							<BillingAccountBrevo billingAccount={record} onChanged={refetch} />
						</Box>
						{/* Le back étend le périmètre BA vers ses projets : on voit donc
						    aussi l'activité projet des clients de ce compte. Colonne BA
						    masquée — elle répéterait la fiche courante à chaque ligne. */}
						<ActivityCard
							filters={{ billingAccountId: String(record.id) }}
							showBillingAccount={false}
						/>
					</Grid>

					<Grid item xs={4}>
						{/* Le statut a deux lectures : sous contrat, c'est la date de fin
						    qui fait foi ; sinon c'est l'abonnement Stripe. Le chip unique
						    d'avant affichait « No active subscription » en rouge et en
						    permanence sur tous les comptes Entreprise, qui n'ont par
						    construction aucun abonnement. */}
						<BillingAccountContract
							billingAccountId={String(record.id)}
							underContract={underContract}
							hasLiveSubscription={hasLiveSubscription}
							onChanged={refetch}
						/>

						<BillingAccountPartner billingAccount={{ id: String(record.id), name: record.name }} />

						<BillingAccountFreeFlag billingAccount={record} onChanged={refetch} />
						<BillingAccountAgentsBetaFlag billingAccount={record} onChanged={refetch} />

						<Card style={{ width: '100%' }}>
							<CardContent>
								<Typography variant="h6" gutterBottom color="error">
									Danger zone
								</Typography>
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
									Permanently delete this billing account and all its data
									(projects, connectors, datasets, subscriptions). Irreversible.
								</Typography>
								<Button
									variant="outlined"
									color="error"
									startIcon={<DeleteIcon />}
									onClick={() => setDeleteOpen(true)}
									fullWidth
								>
									Delete billing account
								</Button>
							</CardContent>
						</Card>

					</Grid>
				</Grid>

				<BillingAccountDeleteModal
					billingAccount={{ id: String(record.id), name: record.name }}
					open={deleteOpen}
					onClose={() => setDeleteOpen(false)}
					onDeleted={() => {
						setDeleteOpen(false);
						navigate('/billing-accounts');
					}}
				/>
			</React.Fragment>
		</EditBase>
	);
};
